import { forwardRef, useId } from 'react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
/**
 * Three sizes, because three tiers have to be tellable apart at a glance.
 * `lg` is the one primary action per view; `sm` is for in-row controls.
 */
type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', className = '', type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={`ui-button ui-button--${variant} ui-button--${size} ${className}`.trim()}
      {...rest}
    />
  );
});

interface FieldProps {
  label: string;
  /**
   * ReactNode rather than string since 8i, so a field can carry its meaning
   * AND its save state in the one slot under it. Widened rather than adding a
   * second slot: two places for text under a field is how a drawer ends up
   * with two ways to say the same thing, which is the problem this drop is
   * fixing.
   */
  hint?: ReactNode | undefined;
  error?: string | undefined;
  /**
   * Keeps the label for screen readers but removes it visually. For fields
   * whose label is already carried by surrounding text (a facts row's dt, an
   * inline add-row) — a second visible label reads as clutter and breaks the
   * baseline alignment.
   */
  labelHidden?: boolean | undefined;
}

function FieldShell({
  label,
  hint,
  error,
  labelHidden,
  fieldId,
  children,
}: FieldProps & { fieldId: string; children: ReactNode }) {
  return (
    <div className="ui-field">
      <label
        className={labelHidden ? 'ui-field-label ui-field-label--hidden' : 'ui-field-label'}
        htmlFor={fieldId}
      >
        {label}
      </label>
      {children}
      {hint && !error ? <p className="ui-field-hint">{hint}</p> : null}
      {error ? <InlineError id={`${fieldId}-error`}>{error}</InlineError> : null}
    </div>
  );
}

export interface InputProps extends FieldProps, InputHTMLAttributes<HTMLInputElement> {}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, labelHidden, id, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <FieldShell label={label} hint={hint} error={error} labelHidden={labelHidden} fieldId={fieldId}>
      <input
        ref={ref}
        id={fieldId}
        className="ui-input"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fieldId}-error` : undefined}
        {...rest}
      />
    </FieldShell>
  );
});

export interface TextareaProps extends FieldProps, TextareaHTMLAttributes<HTMLTextAreaElement> {}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, labelHidden, id, rows = 4, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <FieldShell label={label} hint={hint} error={error} labelHidden={labelHidden} fieldId={fieldId}>
      <textarea
        ref={ref}
        id={fieldId}
        rows={rows}
        className="ui-input ui-textarea"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fieldId}-error` : undefined}
        {...rest}
      />
    </FieldShell>
  );
});

export interface SelectOption {
  value: string;
  label: string;
  /**
   * Rendered but unselectable. Used where a choice exists in the vocabulary
   * yet is not legal right now — showing it greyed says "this is a rule",
   * where hiding it would just look like a list that changes shape.
   */
  disabled?: boolean;
}

/**
 * A named set of options inside a select.
 *
 * Added for the Prospecting tab's batch picker (drop 215), which lists
 * batches under the source they came from — Senku's daily sheet, the LinkedIn
 * connections, the old tracker. A real <optgroup>, not a disabled option
 * pretending to be a heading: a screen reader announces the group name with
 * each option inside it, and a disabled option announces "dimmed" and nothing
 * useful.
 *
 * Additive. A caller that passes only `options` renders exactly what it did
 * before.
 */
export interface SelectGroup {
  label: string;
  options: SelectOption[];
}

export interface SelectProps extends FieldProps, SelectHTMLAttributes<HTMLSelectElement> {
  options: SelectOption[];
  /** Rendered after `options`, each under its own heading. */
  groups?: SelectGroup[];
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, labelHidden, id, options, groups, placeholder, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <FieldShell label={label} hint={hint} error={error} labelHidden={labelHidden} fieldId={fieldId}>
      <select
        ref={ref}
        id={fieldId}
        className="ui-input ui-select"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fieldId}-error` : undefined}
        {...rest}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled ?? false}>
            {option.label}
          </option>
        ))}
        {(groups ?? []).map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled ?? false}>
                {option.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </FieldShell>
  );
});

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'children'
> {
  label: string;
  /**
   * Keep the label for a screen reader, take it out of the layout.
   *
   * Input, Select and Textarea have had this since Drop 8 through FieldShell.
   * Checkbox never did, so a caller with a per-row label — "Complete <task
   * title>" — had no way to say the words are for assistive tech only, and
   * they were painted. In the Deals Tasks table that meant a task title
   * wrapping one character per line inside a 44px column.
   *
   * Required whenever the surrounding row already names the thing. A checkbox
   * with no label at all is not the alternative: it would leave the control
   * unnamed, which is worse than ugly.
   */
  labelHidden?: boolean | undefined;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, labelHidden, id, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <label className="ui-checkbox" htmlFor={fieldId}>
      <input ref={ref} id={fieldId} type="checkbox" {...rest} />
      <span className={labelHidden === true ? 'ui-visually-hidden' : undefined}>{label}</span>
    </label>
  );
});

export function InlineError({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <p id={id} className="ui-inline-error" role="alert">
      {children}
    </p>
  );
}
